import Image from "next/image";
import { useState } from "react";
import { GiCheckMark } from "react-icons/gi";
import { PersistGate } from "redux-persist/integration/react";

import { cartContainsItem } from "../../../api/utils/helpers/cartContainsItem";
import { translationsContent } from "../../../localization/translations";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  addCustomFormulaToCart,
  CustomFormulaItem,
  deleteCustomFormulaFromCart,
  removeAlcoholicBeverages,
  removeCustomFormulaFromCart,
} from "../../../redux/slices/cartSlice";
import { selectRestaurant } from "../../../redux/slices/restaurantSlice";
import { persistor } from "../../../redux/store";
import { useLanguage } from "../../../store/lang.context";
import imageLoader from "../../../utils/loader";
import { returnIndex } from "../../../utils/returnIndex";
import { translate } from "../../../utils/translate";
import CompositionModal from "../../menupage/composition";
import AddButton from "../../ui/button/AddButton";
import SubtractButton from "../../ui/button/SubtractButton";
import classes from "./customSaladItem.module.scss";

interface Props {
  item: CustomFormulaItem;
  id: number;
}

const CustomSaladItemCard = ({ item, id }: Props) => {
  const [modalVisible, setModalVisible] = useState(false);

  const dispatch = useAppDispatch();

  const restaurant = useAppSelector(selectRestaurant);

  const { language } = useLanguage();

  const handleOpenModal = () => {
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleOnClick = (add: boolean) => {
    if (add) {
      dispatch(addCustomFormulaToCart({ id: id }));
    } else {
      dispatch(removeCustomFormulaFromCart({ id: id }));
      dispatch(removeAlcoholicBeverages());
    }
  };
  const handleDeleteFromCart = () => {
    dispatch(deleteCustomFormulaFromCart(id));
    dispatch(removeAlcoholicBeverages());
  };

  return (
    <PersistGate persistor={persistor} loading={<>Loading...</>}>
      <div className={classes.container}>
        <div className={classes.image}>
          <Image
            src="surMesure2.png"
            height="100%"
            width="100%"
            alt="Custom salad"
            loader={imageLoader}
          />
        </div>
        <div className={classes.modify}>
          <span onClick={handleDeleteFromCart}>
            {translate(translationsContent, "supprimerButton", language)}
          </span>
          {!item.recipeTitle && (
            <>
              <span>|</span>
              <span onClick={handleOpenModal}>
                {translate(translationsContent, "modifyButton", language)}
              </span>
            </>
          )}
        </div>
        <div className={classes.details}>
          <h3>
            {translate(translationsContent, "customFormulaHeader", language)}
          </h3>
          {item.recipeTitle && <h4>{item.recipeTitle}</h4>}
          <div className={classes.formulas}>
            {item.bases.length > 0 ||
            item.ingredients.length > 0 ||
            item.sauces.length > 0 ? (
              <p>
                <div className={classes.checkmark}>
                  <GiCheckMark />
                </div>
                <p>
                  {translate(translationsContent, "saladContent", language)}{" "}
                  {`${
                    item.bases.length > 0
                      ? item.bases.map(
                          (base) =>
                            `${base.ingredient[returnIndex(language)]}, `
                        )
                      : ""
                  }
                    ${
                      item.ingredients.length > 0
                        ? item.ingredients.map(
                            (ingredient) =>
                              ` ${ingredient.ingredient[returnIndex(language)]}`
                          )
                        : ""
                    }
                    ${
                      item.sauces.length > 0
                        ? item.sauces.map(
                            (sauce) =>
                              `, ${sauce.ingredient[returnIndex(language)]}`
                          )
                        : ""
                    }
                    ${
                      item.extraSauces.length > 0
                        ? item.extraSauces.map((sauce) => {
                            if (
                              cartContainsItem(
                                sauce.ingredient,
                                item.sauces
                              ) === -1
                            )
                              return `, ${
                                sauce.ingredient[returnIndex(language)]
                              }`;
                          })
                        : ""
                    }
                  `}
                </p>
              </p>
            ) : (
              <></>
            )}
            {item.sides1.length > 0 && (
              <p className={classes.item}>
                <div className={classes.checkmark}>
                  <GiCheckMark />
                </div>
                <p>
                  {item.sides1.map(
                    (side) => ` ${side.ingredient[returnIndex(language)]} `
                  )}
                </p>
              </p>
            )}
            {item.sides2.length > 0 && (
              <p className={classes.item}>
                <div className={classes.checkmark}>
                  <GiCheckMark />
                </div>
                <p>
                  {item.sides2.length > 0 &&
                    item.sides2.map(
                      (side) => ` ${side.ingredient[returnIndex(language)]}`
                    )}
                </p>
              </p>
            )}
          </div>
          <div className={classes.bottomContainer}>
            <p>
              <span>{item.caloriesTotal}</span>{" "}
              {translate(translationsContent, "calories", language)}
            </p>
            <div className={classes.add}>
              <SubtractButton onClick={() => handleOnClick(false)} />
              <div className={classes.quantity}>{item.quantity}</div>
              <AddButton onClick={() => handleOnClick(true)} />
            </div>
            <div className={classes.price}>
              <p className={classes.header}>
                {translate(translationsContent, "ttc", language)}
              </p>
              <p>{(item.formulaTotal * item.quantity).toFixed(2)} €</p>
            </div>
          </div>
        </div>
      </div>
      {modalVisible && (
        <CompositionModal
          handleCloseModal={handleCloseModal}
          categories={restaurant.mainCategories}
          sides={[]}
          id={id}
          custom={true}
          modify={true}
        />
      )}
    </PersistGate>
  );
};

export default CustomSaladItemCard;
